import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TImporterState } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { DatePicker, Select } from '@feathr/components';

type TDateOperator = 'is' | 'is_before' | 'is_after' | 'is_between';

/** An opt-out import is a person import with overrides; a partner import has its own `kind`. */
export type TImportType = 'person' | 'opt-out' | 'partner';

export interface IImportsFilters {
  state__in?: TImporterState[];
  importType?: TImportType;
  date_operator?: TDateOperator;
  date_created__gte?: string;
  date_created__lte?: string;
  date_created?: string;
}

interface IStateOption extends ISelectOption {
  id: TImporterState;
}

interface IDateOperatorOption extends ISelectOption {
  id: TDateOperator;
}

interface ITypeOption extends ISelectOption {
  id: TImportType | '';
}

interface IProps {
  filters: IImportsFilters;
  /** Offer partner imports as a type. Off, the table lists person imports only. */
  includePartnerType?: boolean;
  onChange: (filters: IImportsFilters) => void;
}

function ImportsFilters({ filters, includePartnerType = false }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();

  const stateOptions: IStateOption[] = [
    { id: 'draft', name: t('Draft') },
    { id: 'pending', name: t('Pending') },
    { id: 'complete', name: t('Complete') },
    { id: 'failed', name: t('Failed') },
  ];

  const typeOptions: ITypeOption[] = [
    { id: '', name: t('All types') },
    { id: 'person', name: t('Person import') },
    { id: 'opt-out', name: t('Opt-out import') },
    ...(includePartnerType ? [{ id: 'partner' as const, name: t('Partner import') }] : []),
  ];

  const dateOperatorOptions: IDateOperatorOption[] = [
    { id: 'is', name: t('is') },
    { id: 'is_before', name: t('is before') },
    { id: 'is_after', name: t('is after') },
    { id: 'is_between', name: t('is between') },
  ];

  function handleSelectStates(selectedStates: IStateOption[]): void {
    const newValue =
      selectedStates.length > 0 ? selectedStates.map((state) => state.id) : undefined;

    runInAction(() => {
      filters.state__in = newValue;
    });
  }

  function handleSelectType(selectedType: ITypeOption): void {
    runInAction(() => {
      filters.importType = selectedType.id || undefined;
    });
  }

  function handleSelectDateOperator(selectedOperator: IDateOperatorOption): void {
    runInAction(() => {
      Object.assign(filters, {
        date_operator: selectedOperator.id,
        date_created: undefined,
        date_created__gte: undefined,
        date_created__lte: undefined,
      });
    });
  }

  function handleChangeSingleDate(newDate?: string): void {
    runInAction(() => {
      filters.date_created = newDate;
    });
  }

  function handleChangeStartDate(newDate?: string): void {
    runInAction(() => {
      filters.date_created__gte = newDate;
    });
  }

  function handleChangeEndDate(newDate?: string): void {
    runInAction(() => {
      filters.date_created__lte = newDate;
    });
  }

  const currentTypeValue = typeOptions.find((opt) => opt.id === (filters.importType ?? ''));

  const currentStateValues = filters.state__in
    ? stateOptions.filter((opt) => filters.state__in?.includes(opt.id))
    : [];

  const currentDateOperator = filters.date_operator
    ? dateOperatorOptions.find((opt) => opt.id === filters.date_operator)
    : dateOperatorOptions[0];

  return (
    <>
      <Select
        id={'stateSelect'}
        isMulti={true}
        label={t('Import Status')}
        name={'import-state-select'}
        onSelectMulti={handleSelectStates}
        options={stateOptions}
        placeholder={t('All statuses')}
        value={currentStateValues}
      />
      <Select
        id={'typeSelect'}
        label={t('Import Type')}
        name={'import-type-select'}
        onSelectSingle={handleSelectType}
        options={typeOptions}
        placeholder={t('Select type')}
        value={currentTypeValue}
      />
      <Select
        id={'dateOperatorSelect'}
        label={t('Import Date')}
        name={'date-operator-select'}
        onSelectSingle={handleSelectDateOperator}
        options={dateOperatorOptions}
        placeholder={t('Select operator')}
        value={currentDateOperator}
      />
      {filters.date_operator === 'is_between' ? (
        <>
          <DatePicker
            isClearable={true}
            label={t('Start Date')}
            onDateStrChange={handleChangeStartDate}
            showCalendarIcon={true}
            value={filters.date_created__gte}
          />
          <DatePicker
            isClearable={true}
            label={t('End Date')}
            onDateStrChange={handleChangeEndDate}
            showCalendarIcon={true}
            value={filters.date_created__lte}
          />
        </>
      ) : (
        <DatePicker
          isClearable={true}
          label={t('Date')}
          onDateStrChange={handleChangeSingleDate}
          showCalendarIcon={true}
          value={filters.date_created}
        />
      )}
    </>
  );
}

export default observer(ImportsFilters);
